import React, { useState, useEffect, useMemo } from 'react';
import { AiOutlineSave, AiOutlineClose } from 'react-icons/ai';
import {
  extractValue,
  extractSource,
  extractSourceUser,
  extractSourceAt,
  extractConfidence,
  SOURCE_LABELS,
} from '../utils/metadataUtils';
import { formatDateTime } from '../utils/dateUtils';

const getInputType = (tipo) => {
  switch ((tipo || '').toUpperCase()) {
    case 'INTEGER':
    case 'LONG':
      return 'number';
    case 'DATE':
      return 'date';
    case 'STRING':
    default:
      return 'text';
  }
};

const formatDateForInput = (value) => {
  if (!value) return '';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  } catch {
    return value;
  }
};

const buildSourceTooltip = (source, entry) => {
  if (!source) return '';
  const parts = [`Origen: ${SOURCE_LABELS[source] || source}`];
  const user = extractSourceUser(entry);
  const at = extractSourceAt(entry);
  const confidence = extractConfidence(entry);
  if (user) parts.push(`por ${user}`);
  if (at) {
    const formattedAt = formatDateTime(at, { placeholder: '' });
    if (formattedAt) parts.push(`el ${formattedAt}`);
  }
  if (confidence !== null && confidence !== undefined) {
    parts.push(`confianza ${(confidence * 100).toFixed(0)}%`);
  }
  return parts.join(' · ');
};

const MetadataEditor = ({ metadata, metadataSpec, onSave, onCancel, onGetMetadata }) => {
  const specFields = Array.isArray(metadataSpec) ? metadataSpec : [];
  const metadataMap = useMemo(
    () => (metadata && typeof metadata === 'object' ? metadata : {}),
    [metadata]
  );

  // Reconciliar spec + metadata preservando orden del spec
  const rows = useMemo(() => {
    const acc = [];
    const seen = new Set();
    specFields.forEach(campo => {
      const key = campo.name || campo.nombre;
      if (!key) return;
      seen.add(key.toLowerCase().trim());
      const match = Object.entries(metadataMap).find(
        ([k]) => k.toLowerCase().trim() === key.toLowerCase().trim()
      );
      acc.push({ key, entry: match ? match[1] : undefined, spec: campo });
    });
    Object.entries(metadataMap).forEach(([key, entry]) => {
      if (seen.has(key.toLowerCase().trim())) return;
      acc.push({ key, entry, spec: null });
    });
    return acc;
  }, [specFields, metadataMap]);

  const initialValues = useMemo(() => {
    const acc = {};
    rows.forEach(({ key, entry, spec }) => {
      const raw = extractValue(entry);
      const tipo = spec?.type || spec?.tipo;
      acc[key] = (tipo || '').toUpperCase() === 'DATE' ? formatDateForInput(raw) : (raw ?? '');
    });
    return acc;
  }, [rows]);

  const [values, setValues] = useState(initialValues);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValues(initialValues);
  }, [initialValues]);

  useEffect(() => {
    if (onGetMetadata) onGetMetadata(values);
  }, [values, onGetMetadata]);

  const isDirty = useMemo(() => {
    const keys = new Set([...Object.keys(initialValues), ...Object.keys(values)]);
    for (const k of keys) {
      const a = initialValues[k] ?? '';
      const b = values[k] ?? '';
      if (String(a) !== String(b)) return true;
    }
    return false;
  }, [initialValues, values]);

  // Labels de los campos requeridos que siguen vacíos (para feedback UX)
  const missingRequiredLabels = useMemo(() => {
    const missing = [];
    rows.forEach(({ key, spec }) => {
      const required = !!(spec?.required);
      if (!required) return;
      const v = values[key];
      if (v === '' || v === null || v === undefined) {
        missing.push(spec?.label?.trim() || spec?.name || spec?.nombre || key);
      }
    });
    return missing;
  }, [rows, values]);

  const hasMissingRequired = missingRequiredLabels.length > 0;

  const handleChange = (key, value) => {
    setValues(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(values);
    } catch (e) {
      console.error('Error guardando metadata:', e);
    } finally {
      setSaving(false);
    }
  };

  if (rows.length === 0) {
    return (
      <div style={styles.panel}>
        <div style={styles.emptyState}>
          No hay especificación de metadatos disponible para este tipo de documento.
        </div>
      </div>
    );
  }

  return (
    <div style={styles.panel}>
      <div style={styles.header}>
        <h4 style={styles.title}>Editar Metadatos</h4>
        <span style={styles.headerHint}>
          Modificá los campos necesarios y guardá los cambios.
        </span>
      </div>

      <div style={styles.grid}>
        {rows.map(({ key, entry, spec }) => {
          const label = spec?.label?.trim() || spec?.name || spec?.nombre || key;
          const tipo = spec?.type || spec?.tipo;
          const required = !!(spec?.required);
          const description = spec?.description || spec?.descripcion;
          const source = extractSource(entry);
          const sourceLabel = source ? (SOURCE_LABELS[source] || source) : null;
          const tooltip = buildSourceTooltip(source, entry);
          const inputType = getInputType(tipo);
          const value = values[key] ?? '';
          const isEmptyRequired = required && (value === '' || value === null || value === undefined);

          return (
            <div key={key} style={styles.field}>
              <label style={styles.fieldHeader} htmlFor={`meta-${key}`}>
                <span style={styles.label}>
                  {label}
                  {required && <span style={styles.requiredMark} aria-label="obligatorio">*</span>}
                </span>
                {sourceLabel && (
                  <span style={styles.sourceSideText} title={tooltip}>
                    · {sourceLabel}
                  </span>
                )}
              </label>
              <input
                id={`meta-${key}`}
                data-testid={`field-${key}`}
                type={inputType}
                name={key}
                value={value}
                onChange={(e) => handleChange(key, e.target.value)}
                onFocus={(e) => { e.target.style.borderColor = '#00468B'; e.target.style.boxShadow = '0 0 0 3px rgba(0,70,139,0.12)'; }}
                onBlur={(e) => { e.target.style.borderColor = isEmptyRequired ? '#F59E0B' : '#D1D5DB'; e.target.style.boxShadow = 'none'; }}
                style={{
                  ...styles.input,
                  borderColor: isEmptyRequired ? '#F59E0B' : '#D1D5DB',
                }}
              />
              {description && <div style={styles.description}>{description}</div>}
            </div>
          );
        })}
      </div>

      {hasMissingRequired && (
        <div style={styles.missingRequired} role="alert">
          <strong>Faltan {missingRequiredLabels.length} campo{missingRequiredLabels.length > 1 ? 's' : ''} obligatorio{missingRequiredLabels.length > 1 ? 's' : ''}:</strong>{' '}
          {missingRequiredLabels.join(', ')}
        </div>
      )}

      <div style={styles.footer}>
        <button
          type="button"
          style={styles.cancelButton}
          onClick={onCancel}
          disabled={saving}
        >
          <AiOutlineClose style={styles.buttonIcon} />
          Descartar
        </button>
        <button
          type="button"
          style={{
            ...styles.saveButton,
            ...((isDirty && !saving && !hasMissingRequired) ? {} : styles.saveButtonDisabled),
          }}
          onClick={handleSave}
          disabled={saving || !isDirty || hasMissingRequired}
          title={hasMissingRequired ? `Completá: ${missingRequiredLabels.join(', ')}` : undefined}
        >
          <AiOutlineSave style={styles.buttonIcon} />
          {saving ? 'Guardando…' : 'Guardar'}
        </button>
      </div>
    </div>
  );
};

const styles = {
  panel: {
    padding: '20px 24px 16px',
    backgroundColor: '#FFFFFF',
    borderTop: '1px solid #E5E7EB',
  },
  header: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: '12px',
    marginBottom: '16px',
    flexWrap: 'wrap',
  },
  title: {
    margin: 0,
    fontSize: '14px',
    fontWeight: 600,
    color: '#111827',
    letterSpacing: '0.02em',
  },
  headerHint: {
    fontSize: '12px',
    color: '#6B7280',
  },
  emptyState: {
    textAlign: 'center',
    color: '#6B7280',
    fontSize: '13px',
    fontStyle: 'italic',
    padding: '32px 16px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    columnGap: '32px',
    rowGap: '16px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    minWidth: 0,
  },
  fieldHeader: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '6px',
    flexWrap: 'wrap',
    cursor: 'default',
  },
  label: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#374151',
    letterSpacing: '0.02em',
  },
  requiredMark: {
    color: '#D97706',
    marginLeft: '2px',
    fontWeight: 600,
  },
  sourceSideText: {
    fontSize: '11px',
    fontWeight: 400,
    color: '#9CA3AF',
    cursor: 'help',
  },
  input: {
    width: '100%',
    padding: '8px 10px',
    fontSize: '14px',
    color: '#111827',
    backgroundColor: '#FFFFFF',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  },
  description: {
    fontSize: '11px',
    color: '#6B7280',
    fontStyle: 'italic',
    lineHeight: 1.35,
  },
  missingRequired: {
    marginTop: '16px',
    padding: '10px 12px',
    backgroundColor: '#FEF3C7',
    color: '#92400E',
    border: '1px solid #F59E0B',
    borderRadius: '6px',
    fontSize: '12px',
    lineHeight: 1.4,
  },
  footer: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'flex-end',
    marginTop: '20px',
    paddingTop: '16px',
    borderTop: '1px solid #F3F4F6',
  },
  buttonIcon: {
    fontSize: '15px',
  },
  saveButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 18px',
    backgroundColor: '#00468B',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    letterSpacing: '0.02em',
    transition: 'background-color 0.15s ease, opacity 0.15s ease',
  },
  saveButtonDisabled: {
    backgroundColor: '#9CA3AF',
    cursor: 'not-allowed',
    opacity: 0.7,
  },
  cancelButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    backgroundColor: 'transparent',
    color: '#374151',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 500,
    letterSpacing: '0.02em',
    transition: 'background-color 0.15s ease, border-color 0.15s ease',
  },
};

export default MetadataEditor;
